import clsx from "clsx";
import { FC, RefObject, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { Close, Search } from "@/assets/icons/ds";
import { useDebounce } from "@/hooks/useDebounce";
import { useQueryParam } from "@/hooks/useQueryParam";

interface Props {
  className?: string;
  onEmptyBlur: () => void;
  inputRef?: RefObject<HTMLInputElement | null>;
}

/**
 * The Plans search field: a `dire-input` sized row (64px, aubergine-8 fill)
 * whose trailing slot is the magnifier while empty and a clear button once
 * something has been typed.
 */
export const SearchBar: FC<Props> = ({ className, onEmptyBlur, inputRef }) => {
  const [queryParam, setQueryParams] = useQueryParam("searchTerm");
  const [searchTerm, setSearchTerm] = useState(queryParam);
  const [debouncedSearchTerm] = useDebounce(searchTerm, 200);
  const [, setPaginationPage] = useQueryParam("page");
  const { t } = useTranslation("portal");
  const internalRef = useRef<HTMLInputElement>(null);
  const ref = inputRef || internalRef;

  useEffect(() => {
    if (queryParam !== debouncedSearchTerm) {
      setQueryParams(debouncedSearchTerm, { replace: true });
    }
  }, [queryParam, debouncedSearchTerm, setQueryParams]);

  const onBlur = () => {
    if (!searchTerm) {
      onEmptyBlur();
    }
  };

  const onClear = () => {
    setSearchTerm("");
    ref?.current?.focus();
  };

  return (
    <div
      data-testid="search-bar"
      id="search-bar"
      className={clsx(
        "flex h-14 items-center gap-3 rounded-md bg-surface-tertiary px-5",
        className,
      )}
    >
      <input
        className="w-full min-w-0 bg-transparent text-body-base font-semibold leading-md text-foreground-primary outline-none"
        ref={ref}
        value={searchTerm}
        placeholder={t("cockpit.filter.searchBar.placeholder")}
        aria-label={t("cockpit.filter.searchBar.placeholder")}
        onChange={(event) => {
          setPaginationPage("");
          setSearchTerm(event.target.value);
        }}
        onBlur={onBlur}
      />
      {searchTerm ? (
        <button
          data-testid="search-close-button"
          className="shrink-0 text-foreground-primary"
          aria-label={t("common.buttons.clearSearch")}
          onClick={onClear}
        >
          <Close />
        </button>
      ) : (
        <Search className="shrink-0 text-foreground-primary" />
      )}
    </div>
  );
};
